import { createTheme } from '@mui/material/styles';
import fontSizes from './fontSizes';
import { muiPalette } from './colors';

//Layout
const spacing = 10;

// Standard font family matching the SCSS variable
const fontFamily = '"Roboto", "Helvetica", "Arial", sans-serif';

// CSS variable references - these automatically respond to dark mode
// when the .dark-theme class is applied to the body
const cssVar = (name) => `var(${name})`;

export default createTheme({
  spacing,
  fontSizes,
  borderRadius: 4,
  
  // Typography configuration
  typography: {
    fontFamily,
    fontSize: 14,
    htmlFontSize: 16,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    h1: { fontFamily, fontWeight: 300, fontSize: '6rem' },
    h2: { fontFamily, fontWeight: 300, fontSize: '3.75rem' },
    h3: { fontFamily, fontWeight: 400, fontSize: '3rem' },
    h4: { fontFamily, fontWeight: 400, fontSize: '2.125rem' },
    h5: { fontFamily, fontWeight: 400, fontSize: '1.5rem' },
    h6: { fontFamily, fontWeight: 500, fontSize: '1.25rem' },
    subtitle1: { fontFamily, fontWeight: 400, fontSize: '1rem' },
    subtitle2: { fontFamily, fontWeight: 500, fontSize: '0.875rem' },
    body1: { fontFamily, fontWeight: 400, fontSize: '1rem' },
    body2: { fontFamily, fontWeight: 400, fontSize: '0.875rem' },
    button: { fontFamily, fontWeight: 500, fontSize: '0.875rem', textTransform: 'uppercase' },
    caption: { fontFamily, fontWeight: 400, fontSize: '0.75rem' },
    overline: { fontFamily, fontWeight: 400, fontSize: '0.75rem', textTransform: 'uppercase' },
  },
  
  // Palette imported from colors.js (single source of truth)
  // Component styleOverrides below use CSS variables for dark mode support
  palette: muiPalette,

  // Custom theme values for app-specific styling
  custom: {
    // Card styling
    card: {
      headerBg: cssVar('--card-header-bg-color'),
      headerColor: cssVar('--card-header-font-color'),
      rowHover: cssVar('--card-row-hover'),
    },
    // Button styling
    button: {
      bg: cssVar('--button-bg-color'),
      color: cssVar('--button-font-color'),
      hoverBg: cssVar('--button-hover-bg-color'),
      hoverColor: cssVar('--button-hover-font-color'),
      disabledBg: cssVar('--button-disabled-bg-color'),
      disabledColor: cssVar('--button-disabled-font-color'),
      secondaryBg: cssVar('--button-secondary-bg-color'),
      secondaryColor: cssVar('--button-secondary-font-color'),
    },
    // Grid styling
    grid: {
      headerBg: cssVar('--grid-column-header-bg-color'),
      headerColor: cssVar('--grid-header-font-color'),
      bg: cssVar('--grid-bg-color'),
      borderColor: cssVar('--grid-border-color'),
      hoverColor: cssVar('--grid-hover-color'),
      contentColor: cssVar('--grid-content-font-color'),
    },
    // Page header
    pageHeader: {
      bg: cssVar('--page-header-bg-color'),
      color: cssVar('--page-header-font-color'),
      buttonBg: cssVar('--page-header-button-bg-color'),
      buttonHoverBg: cssVar('--page-header-button-bg-color-hover'),
    },
    // Tooltips
    tooltip: {
      bg: cssVar('--tooltip-bg-color'),
      color: cssVar('--tooltip-font-color'),
      titleBg: cssVar('--tooltip-title-bg-color'),
      titleColor: cssVar('--tooltip-title-font-color'),
      borderColor: cssVar('--tooltip-border-color'),
    },
    // Dropdown/Menu
    dropdown: {
      bg: cssVar('--dropdown-bg-color'),
    },
    // Icons
    icon: {
      primary: cssVar('--primary-icon-color'),
      secondary: cssVar('--secondary-icon-color'),
      disabled: cssVar('--disabled-icon-color'),
    },
    // Links
    link: {
      color: cssVar('--hyperlink-color'),
      hoverColor: cssVar('--hyperlink-hover-color'),
    },
    // Avatar
    avatar: {
      bg: cssVar('--avatar-bg-color'),
      color: cssVar('--avatar-font-color'),
    },
    // Date picker
    datePicker: {
      headerBg: cssVar('--date-picker-date-bg-color'),
      headerColor: cssVar('--date-picker-date-text-color'),
      activeText: cssVar('--date-picker-active-text'),
      clockBg: cssVar('--date-picker-clock-bg-color'),
    },
    // Toast/Snackbar
    toast: {
      errorBg: cssVar('--toast-error-bg-color'),
      color: cssVar('--toast-font-color'),
    },
    // Shadows
    boxShadow: cssVar('--md-box-shadow'),
    // Transitions
    transitionDuration: cssVar('--transition-duration'),
  },

  components: {
    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: `${spacing * .4}px ${spacing * 1.6}px`,
          backgroundColor: cssVar('--card-header-bg-color'),
          color: cssVar('--card-header-font-color'),
        },
        title: {
          fontWeight: 'normal',
          fontSize: '1rem',
          color: 'inherit',
        },
        subheader: {
          color: 'inherit',
          opacity: 0.9,
        },
        action: {
          marginTop: -3,
          color: 'inherit',
        }
      }
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: spacing,
          backgroundColor: cssVar('--card-bg-color'),
          color: cssVar('--primary-font-color'),
          '&:last-child': {
            paddingBottom: spacing,
          },
        },
      }
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          justifyContent: 'flex-end',
          backgroundColor: cssVar('--card-bg-color'),
        },
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: cssVar('--card-bg-color'),
          boxShadow: cssVar('--md-box-shadow'),
          color: cssVar('--primary-font-color'),
        },
      }
    },
    MuiButton: {
      defaultProps: {
        variant: 'contained',
        color: 'primary',
      },
      styleOverrides: {
        root: {
          textTransform: 'capitalize',
        },
        contained: {
          backgroundColor: cssVar('--button-bg-color'),
          color: cssVar('--button-font-color'),
          '&:hover': {
            backgroundColor: cssVar('--button-hover-bg-color'),
            color: cssVar('--button-hover-font-color'),
          },
          '&.Mui-disabled': {
            backgroundColor: cssVar('--button-disabled-bg-color'),
            color: cssVar('--button-disabled-font-color'),
          },
        },
        containedSecondary: {
          backgroundColor: cssVar('--button-secondary-bg-color'),
          color: cssVar('--button-secondary-font-color'),
        },
        outlined: {
          borderColor: cssVar('--button-bg-color'),
          color: cssVar('--button-bg-color'),
          '&:hover': {
            borderColor: cssVar('--button-hover-bg-color'),
            backgroundColor: 'transparent',
          },
        },
        text: {
          color: cssVar('--button-bg-color'),
          '&:hover': {
            backgroundColor: cssVar('--hover-color'),
          },
        },
      }
    },
    MuiIconButton: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: {
          color: cssVar('--primary-icon-color'),
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: cssVar('--hover-color'),
          },
          '&.Mui-disabled': {
            color: cssVar('--disabled-icon-color'),
          },
        },
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: cssVar('--card-bg-color'),
          boxShadow: cssVar('--md-box-shadow'),
        },
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-root': {
            color: cssVar('--primary-font-color'),
          },
          '& .MuiInputLabel-root': {
            color: cssVar('--tertiary-font-color'),
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: cssVar('--default-border-color'),
          },
          '& .MuiInput-underline:before': {
            borderBottomColor: cssVar('--default-border-color'),
          },
          '& .MuiInput-underline:after': {
            borderBottomColor: cssVar('--primary-color'),
          },
          '& .Mui-disabled': {
            color: cssVar('--disabled-font-color'),
          },
        },
      }
    },
    MuiInput: {
      styleOverrides: {
        root: {
          color: cssVar('--primary-font-color'),
          '&:before': {
            borderBottomColor: cssVar('--default-border-color'),
          },
          '&:after': {
            borderBottomColor: cssVar('--primary-color'),
          },
          '&:hover:not(.Mui-disabled):before': {
            borderBottomColor: cssVar('--primary-color'),
          },
        },
        input: {
          color: cssVar('--primary-font-color'),
        },
      }
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          color: cssVar('--primary-font-color'),
          '&.Mui-disabled': {
            color: cssVar('--disabled-font-color'),
          },
        },
        input: {
          '&::placeholder': {
            color: cssVar('--tertiary-font-color'),
            opacity: 1,
          },
        },
      }
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          color: cssVar('--primary-font-color'),
        },
        icon: {
          color: cssVar('--primary-icon-color'),
        },
      }
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: cssVar('--dropdown-bg-color'),
          boxShadow: cssVar('--md-box-shadow'),
        },
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: cssVar('--primary-font-color'),
          '&:hover': {
            backgroundColor: cssVar('--hover-color'),
          },
          '&.Mui-selected': {
            backgroundColor: cssVar('--hover-color'),
            '&:hover': {
              backgroundColor: cssVar('--hover-color'),
            },
          },
        },
      }
    },
    MuiList: {
      styleOverrides: {
        root: {
          backgroundColor: cssVar('--dropdown-bg-color'),
        },
      }
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          color: cssVar('--primary-font-color'),
          '&:hover': {
            backgroundColor: cssVar('--hover-color'),
          },
          '&.Mui-selected': {
            backgroundColor: cssVar('--hover-color'),
          },
        },
      }
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          backgroundColor: cssVar('--default-border-color'),
        },
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: cssVar('--card-bg-color'),
          boxShadow: cssVar('--md-box-shadow'),
        },
      }
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          backgroundColor: cssVar('--card-header-bg-color'),
          color: cssVar('--card-header-font-color'),
        },
      }
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          color: cssVar('--primary-font-color'),
          fontSize: '1rem',
        },
        // Workaround for MUI bug where DialogContent padding-top is removed when following DialogTitle.
        // We use the dividers prop on DialogContent and make them invisible.
        // See: https://github.com/mui/material-ui/issues/27851
        dividers: {
          borderColor: 'transparent',
        },
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          color: cssVar('--grid-content-font-color'),
          borderColor: cssVar('--grid-border-color'),
        },
        head: {
          backgroundColor: cssVar('--grid-column-header-bg-color'),
          color: cssVar('--grid-header-font-color'),
        },
      }
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: cssVar('--grid-hover-color'),
          },
        },
      }
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: cssVar('--primary-color'),
        },
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: cssVar('--primary-font-color'),
          '&.Mui-selected': {
            color: cssVar('--primary-color'),
          },
        },
      }
    },
    MuiSwitch: {
      styleOverrides: {
        track: {
          backgroundColor: cssVar('--translucent-black'),
        },
        switchBase: {
          '&.Mui-checked': {
            color: cssVar('--primary-color'),
            '& + .MuiSwitch-track': {
              backgroundColor: cssVar('--primary-color'),
            },
          },
        },
      }
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: cssVar('--default-border-color'),
          '&.Mui-checked': {
            color: cssVar('--primary-color'),
          },
        },
      }
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: cssVar('--default-border-color'),
          '&.Mui-checked': {
            color: cssVar('--primary-color'),
          },
        },
      }
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: cssVar('--primary-color'),
        },
      }
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          backgroundColor: cssVar('--secondary-color'),
        },
        bar: {
          backgroundColor: cssVar('--primary-color'),
        },
      }
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: cssVar('--avatar-bg-color'),
          color: cssVar('--avatar-font-color'),
        },
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          color: cssVar('--primary-font-color'),
        },
        filled: {
          backgroundColor: cssVar('--secondary-color'),
          color: cssVar('--primary-font-color'),
        },
        colorPrimary: {
          backgroundColor: cssVar('--primary-color'),
          color: cssVar('--secondary-font-color'),
        },
        outlinedPrimary: {
          backgroundColor: 'transparent',
          color: cssVar('--primary-font-color'),
          borderColor: cssVar('--primary-color'),
        },
        outlinedSecondary: {
          backgroundColor: 'transparent',
          color: cssVar('--primary-font-color'),
          borderColor: cssVar('--secondary-color'),
        },
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: cssVar('--tooltip-bg-color'),
          color: cssVar('--tooltip-font-color'),
          border: `1px solid ${cssVar('--tooltip-border-color')}`,
        },
      }
    },
    MuiAlert: {
      styleOverrides: {
        standardError: {
          backgroundColor: cssVar('--toast-error-bg-color'),
          color: cssVar('--toast-font-color'),
        },
        filledSuccess: {
          backgroundColor: cssVar('--toast-success-bg-color'),
          color: cssVar('--toast-font-color'),
        },
        filledError: {
          backgroundColor: cssVar('--toast-error-bg-color'),
          color: cssVar('--toast-font-color'),
        },
        filledWarning: {
          backgroundColor: cssVar('--toast-warning-bg-color'),
          color: cssVar('--toast-font-color'),
        },
        filledInfo: {
          backgroundColor: cssVar('--toast-info-bg-color'),
          color: cssVar('--toast-font-color'),
        },
      }
    },
    MuiSnackbarContent: {
      styleOverrides: {
        root: {
          color: cssVar('--toast-font-color'),
        },
      }
    },
    MuiLink: {
      defaultProps: {
        color: 'primary',
        underline: 'hover',
      },
      styleOverrides: {
        root: {
          color: cssVar('--primary-color'),
          '&:hover': {
            color: cssVar('--primary-color-darker'),
          },
        },
      }
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          display: 'flex',
          alignItems: 'center',
        },
      }
    },
    MuiStepper: {
      styleOverrides: {
        root: {
          backgroundColor: cssVar('--case-stepper-bg-color'),
          paddingTop: cssVar('--case-stepper-top-bottom-padding'),
          paddingBottom: cssVar('--case-stepper-top-bottom-padding'),
        },
      }
    },
    MuiStepLabel: {
      styleOverrides: {
        label: {
          color: cssVar('--primary-font-color'),
          marginTop: 'var(--margin-10px)',
          '&.Mui-active': {
            color: cssVar('--primary-font-color'),
            fontWeight: 'bold',
          },
          '&.Mui-completed': {
            color: cssVar('--primary-font-color'),
          },
          '&.Mui-disabled': {
            color: cssVar('--secondary-font-color'),
          },
          // Ensure future/pending steps also use theme color
          '&:not(.Mui-active):not(.Mui-completed)': {
            color: cssVar('--primary-font-color'),
          },
        },
      }
    },
    MuiStepConnector: {
      styleOverrides: {
        line: {
          borderColor: cssVar('--case-stepper-line-color'),
        },
        alternativeLabel: {
          top: 15,
        },
        root: {
          '&.Mui-disabled .MuiStepConnector-line': {
            borderColor: cssVar('--case-stepper-line-color-disabled'),
          },
        },
      }
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: cssVar('--primary-font-color'),
        },
      }
    },
    // Date Picker components from @mui/x-date-pickers
    MuiPickersPopper: {
      styleOverrides: {
        paper: {
          backgroundColor: cssVar('--card-bg-color'),
          color: cssVar('--primary-font-color'),
        },
      }
    },
    MuiDateCalendar: {
      styleOverrides: {
        root: {
          backgroundColor: cssVar('--card-bg-color'),
          color: cssVar('--primary-font-color'),
        },
      }
    },
    MuiPickersCalendarHeader: {
      styleOverrides: {
        root: {
          color: cssVar('--primary-font-color'),
        },
        label: {
          color: cssVar('--primary-font-color'),
        },
        switchViewButton: {
          color: cssVar('--primary-icon-color'),
        },
      }
    },
    MuiPickersArrowSwitcher: {
      styleOverrides: {
        button: {
          color: cssVar('--primary-icon-color'),
        },
      }
    },
    MuiDayCalendar: {
      styleOverrides: {
        weekDayLabel: {
          color: cssVar('--tertiary-font-color'),
        },
      }
    },
    MuiPickersDay: {
      styleOverrides: {
        root: {
          color: cssVar('--primary-font-color'),
          '&:hover': {
            backgroundColor: cssVar('--hover-color'),
          },
          '&.Mui-selected': {
            backgroundColor: cssVar('--primary-color'),
            color: cssVar('--secondary-font-color'),
            '&:hover': {
              backgroundColor: cssVar('--primary-color'),
            },
          },
          '&.MuiPickersDay-today': {
            borderColor: cssVar('--primary-color'),
          },
        },
        dayOutsideMonth: {
          color: cssVar('--disabled-font-color'),
        },
      }
    },
    MuiPickersYear: {
      styleOverrides: {
        yearButton: {
          color: cssVar('--primary-font-color'),
          '&:hover': {
            backgroundColor: cssVar('--hover-color'),
          },
          '&.Mui-selected': {
            backgroundColor: cssVar('--primary-color'),
            color: cssVar('--secondary-font-color'),
          },
        },
      }
    },
    MuiPickersMonth: {
      styleOverrides: {
        monthButton: {
          color: cssVar('--primary-font-color'),
          '&:hover': {
            backgroundColor: cssVar('--hover-color'),
          },
          '&.Mui-selected': {
            backgroundColor: cssVar('--primary-color'),
            color: cssVar('--secondary-font-color'),
          },
        },
      }
    },
    MuiClock: {
      styleOverrides: {
        root: {
          backgroundColor: cssVar('--date-picker-clock-bg-color'),
        },
        clock: {
          backgroundColor: cssVar('--date-picker-clock-bg-color'),
        },
      }
    },
    MuiClockNumber: {
      styleOverrides: {
        root: {
          color: cssVar('--primary-font-color'),
          '&.Mui-selected': {
            backgroundColor: cssVar('--primary-color'),
            color: cssVar('--secondary-font-color'),
          },
        },
      }
    },
    MuiClockPointer: {
      styleOverrides: {
        root: {
          backgroundColor: cssVar('--primary-color'),
        },
        thumb: {
          backgroundColor: cssVar('--primary-color'),
          borderColor: cssVar('--primary-color'),
        },
      }
    },
    MuiTimeClock: {
      styleOverrides: {
        root: {
          backgroundColor: cssVar('--card-bg-color'),
        },
      }
    },
    MuiMultiSectionDigitalClock: {
      styleOverrides: {
        root: {
          backgroundColor: cssVar('--card-bg-color'),
        },
      }
    },
    MuiMultiSectionDigitalClockSection: {
      defaultProps: {
        autoFocus: false,
        formatDensity: "spacious",
      },
      styleOverrides: {
        root: {
          color: cssVar('--primary-font-color'),
        },
        item: {
          color: cssVar('--primary-font-color'),
          '&:hover': {
            backgroundColor: cssVar('--hover-color'),
          },
          '&.Mui-selected': {
            backgroundColor: cssVar('--primary-color'),
            color: cssVar('--secondary-font-color'),
            '&:hover': {
              backgroundColor: cssVar('--primary-color'),
            },
          },
        },
      }
    },
  },
});
